import { httpClient } from "@/lib/http";
import type { ApiResponse } from "@/types/api";

export type ApiDimension = {
	description: string;
	id: string;
	name: string;
	shortName: string;
	sortOrder: number;
	userExplanation: string;
};

export type ApiSubskill = {
	definition: string;
	dimensionId: string;
	id: string;
	name: string;
	sortOrder: number;
};

export type ApiEvidenceRule = {
	dimensionId: string;
	exerciseId: string | null;
	id: string;
	ruleType: "score" | "no_score";
	sortOrder: number;
	text: string;
};

export type ApiExerciseDimensionScore = {
	dimensionId: string;
	exerciseId: string;
	normalizedWeight: number;
	rawScore: number;
	reviewedBy: string;
	source: string;
	updatedAt: string;
	version: string;
};

export type ApiPractice = {
	actionStepsJson?: string;
	actionStrategy: string;
	appSurface: string;
	author: string;
	directoryRank?: number | null;
	difficultyTag: string;
	dimensionId: string;
	displayDimensionId?: string;
	dimensionScores?: ApiExerciseDimensionScore[];
	estimatedMinutes: number;
	formTag: string;
	id: string;
	isDirectoryFeatured?: boolean;
	isNative: number;
	isShareable: number;
	originalTheme: string;
	recordPrompt?: string;
	socialTag: string;
	sourceOrder: number;
	theorySource: string;
	title: string;
};

export type ApiTimelineItem = {
	audioKey?: string | null;
	authorName?: string;
	content: string;
	createdAt: string;
	dimensionId?: string;
	exerciseId?: string;
	feedbackScore?: number | null;
	friendName?: string | null;
	friendTag?: string | null;
	id: string;
	image: string | null;
	imageKey: string | null;
	kind: "practice" | "note";
	mode: string;
	reactions?: {
		seen: number;
		support: number;
	};
	myReactions?: {
		seen: boolean;
		support: boolean;
	};
	summaryKeyword?: string;
	targetGroupId?: string | null;
	targetGroupName?: string | null;
	text: string;
	time: string;
	title?: string;
	userId?: string;
	visibility?: "private" | "group" | "friend";
	voice: number;
	voiceSeconds: number;
};

export type ApiRelationAsset = {
	createdAt: string;
	id: string;
	name: string;
	sourceKind: "practice" | "note";
	sourceNoteId: string | null;
	sourceRecordId: string | null;
	sourceTitle: string;
	tag: string;
	visibility: string;
};

export type ApiBadge = {
	description: string;
	dimensionId: string;
	icon: string;
	id: string;
	name: string;
	sortOrder: number;
	unlockRule: string;
	unlockedAt: string | null;
};

export type ApiProgramDay = {
	dayNumber: number;
	exerciseId: string | null;
	focusText: string;
	id: string;
	programId: string;
	title: string;
};

export type ApiProgram = {
	completedDays: number;
	currentDay: number;
	days: ApiProgramDay[];
	description: string;
	dimensionId: string;
	id: string;
	rewardText: string;
	sortOrder: number;
	status: string;
	title: string;
	totalDays: number;
};

export type ApiGroup = {
	description: string;
	dimensionId: string | null;
	id: string;
	joined: boolean;
	memberCount: number;
	name: string;
	session: {
		dimensionId: string | null;
		groupId: string;
		id: string;
		scheduledLabel: string;
		title: string;
	} | null;
	sortOrder: number;
};

export type ApiGrowthReport = {
	focusDimension: string;
	headline: string;
	nextDecision: string;
	ranges: Array<{
		days: number;
		dimensions: Array<{ count?: number; dimensionId: string; score?: number }>;
		total: number;
	}>;
};

export type ApiEncouragement = {
	id: string;
	scene: string;
	sortOrder: number;
	text: string;
	tone: string;
};

export type ApiTodayDraw = {
	availableMinutes: number;
	completed: boolean;
	completedAt: string | null;
	createdAt: string;
	dateKey: string;
	id: string;
	openingLine: string;
	practice: ApiPractice;
	reason: string;
	recommendedDimension: string;
	recordId: string | null;
};

export type ApiDailyKeyword = {
	alreadyConfirmed?: boolean;
	createdAt: string;
	dateKey: string;
	keyword: string;
	userId: string;
};

export type ApiStateEncouragement = {
	content: string;
	id: string | null;
	matchType: "default" | "matched" | "safety";
};

export type ApiStateEntry = {
	createdAt: string;
	dateKey: string;
	encouragement: ApiStateEncouragement;
	id: string;
	text: string;
};

export type ApiOngoingPractice = {
	checked: boolean;
	days: number;
	exerciseId: string;
	id: string;
	startedAt: string;
	status: string;
	todayIndex: number;
	updatedAt: string;
	week: number[];
};

export type ApiUserDimensionScore = {
	dimensionId: string;
	lastPracticedAt: string | null;
	score28d: number;
	score7d: number;
	scoreTotal: number;
};

export type ApiDimensionScoreDelta = {
	baseWeight: number;
	deltaScore: number;
	dimensionId: string;
	evidenceMultiplier: number;
	feedbackMultiplier: number;
	reason: string;
};

export type ApiEvidenceExplanation = {
	maxScore: number;
	rawScore: number;
	rules: Array<{
		hint: string;
		id: string;
		label: string;
		matched: boolean;
		points: number;
	}>;
	score: number;
};

export type GrowthBootstrap = {
	badges: ApiBadge[];
	currentStateEntry: ApiStateEntry | null;
	dailyEncouragement: string;
	dailyKeyword: ApiDailyKeyword | null;
	dimensionScores: ApiUserDimensionScore[];
	dimensions: ApiDimension[];
	encouragements: ApiEncouragement[];
	evidenceRules: ApiEvidenceRule[];
	feedbacks: Array<{
		createdAt: string;
		exerciseId: string | null;
		id: string;
		nextAction: string;
		reason: string;
		recordId: string | null;
		score: number;
	}>;
	groupTimeline: ApiTimelineItem[];
	groups: ApiGroup[];
	ongoing: ApiOngoingPractice[];
	practices: ApiPractice[];
	profile: {
		joined: string;
		mediaCount: number;
		notesCount: number;
		steadyDays: number;
		todayCompleted: number;
		totalPractice: number;
		wellbeing: number;
	};
	programs: ApiProgram[];
	relations: ApiRelationAsset[];
	report: ApiGrowthReport;
	stats: Array<{
		count?: number;
		dimensionId: string;
		score28d?: number;
		score7d?: number;
		scoreTotal?: number;
	}>;
	subskills: ApiSubskill[];
	todayDraw: ApiTodayDraw | null;
	timeline: ApiTimelineItem[];
};

export type GrowthDraw = {
	alreadyDrawn: boolean;
	completed: boolean;
	practice: ApiPractice;
	reason: string;
	recommendedDimension: string;
	todayDraw: ApiTodayDraw;
};

export type PracticeRecordRequest = {
	audioKey?: string | null;
	content: string;
	dateKey?: string;
	exerciseId: string;
	imageKey?: string | null;
	mode: "text" | "image" | "voice";
	summaryKeyword?: string;
	voiceSeconds?: number;
};

export type DailyKeywordRequest = {
	dateKey?: string;
	keyword: string;
};

export type DailyKeywordResponse = ApiDailyKeyword;

export type PracticeRecordResponse = {
	badges: ApiBadge[];
	dimensionDeltas?: ApiDimensionScoreDelta[];
	evidenceExplanation?: ApiEvidenceExplanation;
	evidenceScore: number;
	id: string;
	nextAction: string;
	nextSuggestion: string;
	programProgress: {
		completedDays: number;
		currentDay: number;
		id: string;
		programId: string;
		status: string;
		totalDays: number;
	} | null;
};

export type NoteRequest = {
	audioKey?: string | null;
	authorName?: string;
	content: string;
	friendName?: string;
	friendTag?: string;
	imageKey?: string | null;
	mode: "text" | "image" | "voice";
	targetGroupId?: string | null;
	visibility?: "private" | "group" | "friend";
	voiceSeconds?: number;
};

export type FeedbackRequest = {
	exerciseId?: string;
	reason?: string;
	recordId?: string;
	score: number;
};

export type FeedbackResponse = {
	dimensionDeltas?: ApiDimensionScoreDelta[];
	id: string;
	nextAction: string;
	nextSuggestion: string;
	score: number;
};

export type RelationAssetRequest = {
	name: string;
	sourceKind?: "practice" | "note";
	sourceNoteId?: string | null;
	sourceRecordId?: string | null;
	sourceTitle?: string;
	tag: string;
	visibility?: string;
};

export type GroupReactionResponse = {
	active: boolean;
	myReactions: {
		seen: boolean;
		support: boolean;
	};
	noteId: string;
	reactionType: "seen" | "support";
	reactions: {
		seen: number;
		support: number;
	};
};

export type StartProgramResponse = {
	goal: {
		dimensionId: string;
		id: string;
		title: string;
	};
	program: {
		completedDays: number;
		currentDay: number;
		id: string;
		progressId: string;
		status: string;
		totalDays: number;
	};
};

export const growthBootstrapKey = "/growth/bootstrap";

export const getClientDateKey = () => {
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const day = String(now.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

export const growthBootstrapCacheKey = (userId: string, dateKey = getClientDateKey()) =>
	[growthBootstrapKey, userId, dateKey] as const;

export function getGrowthBootstrap(): Promise<ApiResponse<GrowthBootstrap>> {
	return httpClient.get(growthBootstrapKey, { params: { dateKey: getClientDateKey() } });
}

export function drawGrowthPractice(data: {
	availableMinutes?: number;
	openingLine: string;
}): Promise<ApiResponse<GrowthDraw>> {
	return httpClient.post("/growth/draw", { ...data, dateKey: getClientDateKey() });
}

export function confirmDailyKeyword(
	data: DailyKeywordRequest,
): Promise<ApiResponse<DailyKeywordResponse>> {
	return httpClient.post("/growth/daily-keyword", { ...data, dateKey: getClientDateKey() });
}

export function createStateEntry(data: {
	needLabel?: string;
	stateLabel?: string;
	text: string;
}): Promise<ApiResponse<ApiStateEntry>> {
	return httpClient.post("/encouragement/state-entries", {
		...data,
		dateKey: getClientDateKey(),
	});
}

export function createPracticeRecord(
	data: PracticeRecordRequest,
): Promise<ApiResponse<PracticeRecordResponse>> {
	return httpClient.post("/growth/practice-records", { ...data, dateKey: getClientDateKey() });
}

export function toggleOngoingPractice(data: { exerciseId: string }): Promise<
	ApiResponse<{
		active: boolean;
		exerciseId: string;
		id: string;
	}>
> {
	return httpClient.post("/growth/ongoing", data);
}

export function startGrowthProgram(data: {
	dimensionId?: string;
	programId?: string;
}): Promise<ApiResponse<StartProgramResponse>> {
	return httpClient.post("/growth/programs/start", data);
}

export function setGrowthGoal(data: {
	dimensionId: string;
	title?: string;
}): Promise<ApiResponse<{ dimensionId: string; id: string; title: string }>> {
	return httpClient.post("/growth/goals", data);
}

export function createNote(
	data: NoteRequest,
): Promise<
	ApiResponse<{ badges: ApiBadge[]; id: string; relationAsset: ApiRelationAsset | null }>
> {
	return httpClient.post("/growth/notes", data);
}

export function createPracticeFeedback(
	data: FeedbackRequest,
): Promise<ApiResponse<FeedbackResponse>> {
	return httpClient.post("/growth/feedback", data);
}

export function createRelationAsset(
	data: RelationAssetRequest,
): Promise<ApiResponse<{ asset: ApiRelationAsset; badges: ApiBadge[] }>> {
	return httpClient.post("/growth/relation-assets", data);
}

export function createGroupReaction(data: {
	noteId: string;
	reactionType: "seen" | "support";
}): Promise<ApiResponse<GroupReactionResponse>> {
	return httpClient.post("/growth/group-reactions", data);
}

export function joinGrowthGroup(data: { groupId: string }): Promise<
	ApiResponse<{
		groupId: string;
		joined: boolean;
		name: string;
	}>
> {
	return httpClient.post("/growth/group-members", data);
}
